class RfHero extends HTMLElement {
  connectedCallback() {
    this.slides = [...this.querySelectorAll('[data-slide]')];
    this.index = 0;
    this.dots = [...this.querySelectorAll('[data-slide-to]')];
    this.duration = (Number(this.dataset.duration) || 6) * 1000;
    this.elapsed = 0;
    this.inView = true;
    this.loading = false;
    this.editorSelected = false;
    this.lastTime = performance.now();
    this.request = 0;
    this.controller?.abort();
    this.controller = new AbortController();
    const options = { signal: this.controller.signal };
    this.slides.forEach((slide, i) => {
      slide.hidden = false;
      slide.inert = i !== 0;
      slide.setAttribute('aria-hidden', String(i !== 0));
    });
    this.setAttribute('data-ready', '');
    this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.show(i), options));
    this.addEventListener('keydown', event => {
      if (!['ArrowLeft', 'ArrowRight'].includes(event.key) || /INPUT|TEXTAREA|SELECT/.test(event.target.tagName)) return;
      event.preventDefault();
      this.show(this.index + (event.key === 'ArrowLeft' ? -1 : 1));
    }, options);
    this.addEventListener('touchstart', event => {
      this.touch = event.touches.length === 1 ? { x: event.touches[0].clientX, y: event.touches[0].clientY } : null;
    }, { ...options, passive: true });
    this.addEventListener('touchend', event => {
      if (!this.touch || !event.changedTouches[0]) return;
      const dx = event.changedTouches[0].clientX - this.touch.x;
      const dy = event.changedTouches[0].clientY - this.touch.y;
      this.touch = null;
      if (Math.abs(dx) > 45 && Math.abs(dx) > Math.abs(dy) * 1.3) {
        this.suppressClickUntil = performance.now() + 400;
        this.show(this.index + (dx < 0 ? 1 : -1));
      }
    }, { ...options, passive: true });
    this.addEventListener('click', event => {
      if (performance.now() < (this.suppressClickUntil || 0)) { event.preventDefault(); event.stopPropagation(); }
    }, { ...options, capture: true });
    document.addEventListener('shopify:block:select', event => {
      const index = this.slides.indexOf(event.target.closest('[data-slide]'));
      if (index >= 0) { this.editorSelected = true; this.show(index); }
    }, options);
    document.addEventListener('shopify:block:deselect', () => { this.editorSelected = false; }, options);
    document.addEventListener('visibilitychange', () => { this.lastTime = performance.now(); this.syncVideos(); }, options);
    this.observer?.disconnect();
    this.observer = new IntersectionObserver(entries => { this.inView = entries[0].isIntersecting; this.lastTime = performance.now(); this.syncVideos(); });
    this.observer.observe(this);
    cancelAnimationFrame(this.frame);
    const tick = now => {
      const delta = Math.min(now - this.lastTime, 100);
      this.lastTime = now;
      const keyboardFocus = this.querySelector(':focus-visible');
      if (this.slides.length > 1 && this.inView && !document.hidden && !this.loading && !this.editorSelected && !keyboardFocus) {
        this.elapsed += delta;
        this.style.setProperty('--hero-progress', String(Math.min(this.elapsed / this.duration, 1)));
        if (this.elapsed >= this.duration) this.show(this.index + 1);
      }
      this.frame = requestAnimationFrame(tick);
    };
    this.frame = requestAnimationFrame(tick);
    this.syncVideos();
  }
  disconnectedCallback() {
    this.request++;
    cancelAnimationFrame(this.frame);
    this.observer?.disconnect();
    this.controller?.abort();
    this.querySelectorAll('video').forEach(video => video.pause());
  }
  async show(index) {
    if (!this.slides.length) return;
    const next = (index + this.slides.length) % this.slides.length;
    const request = ++this.request;
    this.loading = true;
    const image = this.slides[next].querySelector('img');
    if (image) {
      image.loading = 'eager';
      await Promise.race([image.decode().catch(() => {}), new Promise(resolve => setTimeout(resolve, 1200))]);
    }
    if (request !== this.request || !this.isConnected) return;
    this.index = next;
    this.slides.forEach((slide, i) => {
      slide.inert = i !== next;
      slide.setAttribute('aria-hidden', String(i !== next));
      slide.classList.toggle('is-current', i === next);
    });
    this.elapsed = 0;
    this.loading = false;
    this.lastTime = performance.now();
    this.style.setProperty('--hero-progress', '0');
    this.dots.forEach((dot, i) => { dot.classList.toggle('is-active', i === next); dot.setAttribute('aria-pressed', String(i === next)); });
    this.syncVideos();
  }
  syncVideos() {
    this.slides.forEach((slide, i) => slide.querySelectorAll('video').forEach(video => {
      if (i === this.index && !document.hidden && this.inView) video.play().catch(() => {});
      else video.pause();
    }));
  }
}
if (!customElements.get('rf-hero')) customElements.define('rf-hero', RfHero);


// Native disclosures keep footer navigation usable with keyboard and without JS.
class RfFooterNav extends HTMLElement {
  connectedCallback() {
    this.media = window.matchMedia('(max-width: 749px)');
    this.sync = () => this.querySelectorAll('details').forEach(detail => {
      detail.open = !this.media.matches;
      const summary = detail.querySelector('summary');
      summary.tabIndex = this.media.matches ? 0 : -1;
      if (this.media.matches) summary.removeAttribute('aria-disabled');
      else summary.setAttribute('aria-disabled', 'true');
    });
    this.keepDesktopOpen = event => {
      if (!this.media.matches && event.target.closest('summary')) event.preventDefault();
    };
    this.addEventListener('click', this.keepDesktopOpen);
    this.media.addEventListener('change', this.sync);
    this.sync();
  }
  disconnectedCallback() {
    this.removeEventListener('click', this.keepDesktopOpen);
    this.media.removeEventListener('change', this.sync);
  }
}
if (!customElements.get('rf-footer-nav')) customElements.define('rf-footer-nav', RfFooterNav);
